import React, { useState } from "react";
import { useLanguage, useT } from "../../lib/LanguageContext";
import { AFFILIATE_KINDS, COMMISSION_STATES, AFFILIATE_PATHS } from "../../affiliate/contract";
import { formatMoneyCode } from "../../lib/formatMoney";
import LoadFailure from "../../components/ui/LoadFailure";
import usePagedList from "../../hooks/usePagedList";
import CommissionStatusBadge from "./CommissionStatusBadge";
import { affiliateErrorText } from "./errorText";
import { fmtDate, fmtBps, EMPTY } from "./format";
import Pager from "../../components/ui/Pager";

export const KIND_KEYS = { saas: "aff.kind.saas", marketplace: "aff.kind.marketplace" };
export const STATE_KEYS = {
  accrued: "aff.state.accrued",
  partially_reversed: "aff.state.partially_reversed",
  reversed: "aff.state.reversed",
};

/**
 * Every commission this affiliate has earned, and what has been taken back.
 *
 * `baseCents` IS THE PLATFORM'S OWN SETTLED TAKE, never the sale — which is what makes the
 * program self-funding by construction, and why the column is labelled as such rather than
 * as "amount". A reader who takes it for the order total will think the rate is absurdly
 * small; one who is told what it is can check the arithmetic.
 *
 * `productType` is `null` on every `saas` row: a subscription has no product nature. The
 * placeholder says so rather than inventing a category.
 */
export default function CommissionsTable() {
  const t = useT("admin");
  const tv = useT("common");
  const { lang } = useLanguage();
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");

  const list = usePagedList(AFFILIATE_PATHS.tenantCommissions, {
    ...(kind ? { kind } : {}),
    ...(status ? { status } : {}),
  });

  return (
    <section className="bg-white rounded-2xl border border-eh-border-soft overflow-hidden" data-testid="affiliate-commissions">
      <div className="flex items-center justify-between gap-3 flex-wrap px-5 py-4 border-b border-eh-border-soft">
        <div>
          <h2 className="font-display text-lg text-eh-ink">{t("aff.commissions.title")}</h2>
          <p className="text-xs text-eh-muted mt-0.5">{t("aff.commissions.baseHint")}</p>
        </div>
        <div className="flex gap-2">
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            data-testid="commissions-kind-filter"
            className="bg-white border border-eh-border-soft rounded-lg px-3 py-1.5 text-sm"
          >
            <option value="">{t("aff.commissions.filterAllKinds")}</option>
            {AFFILIATE_KINDS.map((k) => <option key={k} value={k}>{t(KIND_KEYS[k])}</option>)}
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid="commissions-status-filter"
            className="bg-white border border-eh-border-soft rounded-lg px-3 py-1.5 text-sm"
          >
            <option value="">{t("aff.commissions.filterAllStates")}</option>
            {COMMISSION_STATES.map((s) => <option key={s} value={s}>{t(STATE_KEYS[s])}</option>)}
          </select>
        </div>
      </div>

      {list.loadError ? (
        <div className="p-5">
          <LoadFailure
            message={affiliateErrorText(list.loadError, { t, tv, fallback: tv("errors.loadFailed") })}
            onRetry={list.reload}
            retryLabel={tv("actions.retry")}
            testId="affiliate-commissions-load-error"
            retryTestId="affiliate-commissions-retry"
          />
        </div>
      ) : list.loaded && list.rows.length === 0 ? (
        <div className="p-8 text-center text-eh-muted text-sm" data-testid="affiliate-commissions-empty">
          {t("aff.commissions.empty")}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-eh-muted bg-eh-cream/30">
                <tr>
                  <th className="text-left px-4 py-2">{t("aff.commissions.colCreatedAt")}</th>
                  <th className="text-left px-4 py-2">{t("aff.commissions.colKind")}</th>
                  <th className="text-left px-4 py-2">{t("aff.commissions.colCounterparty")}</th>
                  <th className="text-right px-4 py-2">{t("aff.commissions.colBase")}</th>
                  <th className="text-right px-4 py-2">{t("aff.commissions.colRate")}</th>
                  <th className="text-right px-4 py-2">{t("aff.commissions.colAmount")}</th>
                  <th className="text-right px-4 py-2">{t("aff.commissions.colReversed")}</th>
                  <th className="text-left px-4 py-2">{t("aff.commissions.colStatus")}</th>
                  <th className="text-left px-4 py-2">{t("aff.commissions.colHold")}</th>
                </tr>
              </thead>
              <tbody>
                {list.rows.map((c) => (
                  <tr key={c.id} className="border-t border-eh-border-soft" data-testid={`commission-${c.id}`}>
                    <td className="px-4 py-2 text-eh-muted">{fmtDate(c.createdAt, lang)}</td>
                    <td className="px-4 py-2">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-eh-cream/60">
                        {KIND_KEYS[c.kind] ? t(KIND_KEYS[c.kind]) : c.kind}
                      </span>
                      <span className="block text-[11px] text-eh-muted/70 mt-0.5">{c.productType || EMPTY}</span>
                    </td>
                    <td className="px-4 py-2 text-eh-ink">{c.counterpartyName || EMPTY}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-eh-muted">
                      {formatMoneyCode(c.baseCents, c.currency)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-eh-muted">
                      {t("aff.commissions.ratePct", { pct: fmtBps(c.rateBps) })}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums font-medium">
                      {formatMoneyCode(c.amountCents, c.currency)}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-rose-700">
                      {c.reversedCents ? formatMoneyCode(c.reversedCents, c.currency) : EMPTY}
                    </td>
                    <td className="px-4 py-2">
                      <CommissionStatusBadge
                        status={c.status}
                        label={STATE_KEYS[c.status] ? t(STATE_KEYS[c.status]) : c.status}
                        testId={`commission-status-${c.id}`}
                      />
                    </td>
                    <td className="px-4 py-2 text-eh-muted">{fmtDate(c.holdReleasesAt, lang)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager
            page={list.page}
            pageSize={list.pageSize}
            total={list.total}
            shown={list.rows.length}
            onPage={list.setPage}
            infoText={(vars) => t("aff.page.info", vars)}
            prevLabel={t("aff.page.prev")}
            nextLabel={t("aff.page.next")}
            testId="commissions-page"
          />
        </>
      )}
    </section>
  );
}
